import {
  Grid,
  GridSize,
  IconButton,
  Paper,
  TextField,
  Theme,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import makeStyles from "@mui/styles/makeStyles";
import RoomIcon from "@mui/icons-material/Room";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { Link } from "react-router-dom";
import React, { FC, useEffect, useState } from "react";
import { VccIncidentModel } from "../../../../reducers/states/vcc-incident";
import moment from "moment";
import useUserInfo from "../../../../hooks/user-info";
import { NameOrEmailUtil } from "../../../../utils/user-info-utils";
import { DispatchEventFeedModel } from "../../../../reducers";
import { updateViewportRequest } from "../../../../actions/map";
import { useDispatch } from "react-redux";
import AssociatedEventsItems from "./associated-events-items";
import { JogId, JogMap } from "../../../../reducers/states/agency";

const useStyles = makeStyles((theme: Theme) => ({
  incidentCard: {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
  },
}));

interface IncidentCardSimpleProps {
  props: {
    isCollapsed?: boolean;
    vccIncident: VccIncidentModel;
    selectedIncId?: string;
    dispatchAddtlInfo: DispatchEventFeedModel;
    index?: number;
  };
}

const IncidentCardSimple: FC<IncidentCardSimpleProps> = ({ props }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [createdBy, setCreatedBy] = useUserInfo();
  const [isOver90, setIsOver90] = useState(false);

  useEffect(() => {
    setCreatedBy(props.vccIncident.createdBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.vccIncident.createdBy]);

  const eventCount: number = (!props.isCollapsed && props.vccIncident?.dispatchEvents?.length) || 0;
  const getAssociatedEventItems = () => {
    return (
      <AssociatedEventsItems
        props={{
          dispatchAddtlInfo: props.dispatchAddtlInfo,
          incident: props.vccIncident,
          associatedIdRequests: [],
        }}
      />
    );
  }

  useEffect(() => {
    if (moment().diff(props.vccIncident.createdDate) > 5400000) {
      setIsOver90(true);
    }
  }, [props.vccIncident.createdDate]);

  const getTextField = (label: string, value: string) => {
    return (
      <TextField
        fullWidth
        label={label}
        margin="dense"
        maxRows={4}
        multiline
        name={label}
        size="small"
        type="text"
        value={value}
        variant="standard"
        InputProps={{
          readOnly: true,
        }}
      />
    )
  }

  const getIncidentLinkTooltip = () => {
    const mainMsg = "Navigate to VCC Incident Details and Management Page.";
    const jogIds = props.vccIncident.jogIds?.split(",");
    let jogNames = jogIds?.map(id => (JogMap.get(id)?.shortName)) || [];
    jogNames = jogNames?.filter(x => !!x).sort();

    const jogNameOther = JogMap.get(JogId.Other)?.shortName;

    const AssociatedJogs = () => {
      const label = "Associated JOG";
      if (!jogNames.length || (jogNames.length === 1 && jogNames[0] === jogNameOther)) return <>{label}: None</>;

      // Don't display "Other" if there are other associated JOGs
      jogNames = jogNames.filter(n => n !== jogNameOther);

      if (jogNames.length === 1) return <>{label}: {jogNames[0]}</>;

      return (
        <>
          {label}s<br />
          <ul style={{ margin: "auto", padding: "auto" }}>
            {jogNames.map((n) =>
              <li key={n}>{n}</li>)
            }
          </ul>
        </>
      );
    }


    return (
      <span style={{ whiteSpace: "pre-line" }}>
        {mainMsg}<br />
        <br />
        <AssociatedJogs />
        {props.isCollapsed && (
          <>
            <br />
            <br />
            Location: {props.vccIncident.location}<br />
            Type: {props.vccIncident.type}<br />
            <br />
            Created by {NameOrEmailUtil(createdBy) + " on " + moment(props.vccIncident.startTime).format("LL, HH:mm:ss")}
          </>
        )}
      </span>
    )
  }

  const iconButtonStyle = props.isCollapsed ? { padding: "12px 8px" } : { padding: "12px 6px" };

  const gridProps = {
    alignItems: "center",
    container: true,
    item: true,
    p: props.isCollapsed ? 0 : "0 8px",
    props: {
      direction: "row",
      gap: props.isCollapsed ? 0 : 1,
      wrap: "nowrap",
    },
    // "auto" causes all rows to collapse into one row. 12 is so each row is its own row.
    xs: (props?.isCollapsed ? "auto" : 12) as GridSize,
  }

  return (
    <Paper variant="outlined" className={classes.incidentCard}>
      <Grid container direction={props?.isCollapsed ? "row" : "column"} wrap="wrap" alignItems={"center"}>
        <Grid {...gridProps}>
          {!props.isCollapsed && (
            <Grid item xs={true}>
              {getTextField("Type", props.vccIncident.type)}
            </Grid>
          )}
          <Grid item>
            <Tooltip title={getIncidentLinkTooltip()}>
              <IconButton
                component={Link}
                to={"/incident-model/" + props.vccIncident.id}
                size="large"
                sx={iconButtonStyle}
              >
                <LaunchOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid {...gridProps}>
          {!props.isCollapsed && (
            <Grid item xs={true}>
              {getTextField("Location", props.vccIncident.location)}
            </Grid>
          )}

          <Grid item>
            <Tooltip title="Zoom to VCC Incident on Map">
              <IconButton
                size="large"
                sx={iconButtonStyle}
                onClick={() =>
                  dispatch(updateViewportRequest(props.vccIncident.latitude, props.vccIncident.longitude, 14))
                }
              >
                <RoomIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid {...gridProps}>
          {!props.isCollapsed && (
            <Grid item xs={true}>
              {getTextField("Created by", NameOrEmailUtil(createdBy) + " on " + moment(props.vccIncident.startTime).format("LL, HH:mm:ss"))}
            </Grid>
          )}
          {eventCount === 1 && (
            getAssociatedEventItems()
          )}
          {isOver90 && (
            <Grid item>
              <Tooltip title={(
                <span style={{ whiteSpace: "pre-line" }}>
                  Open for over 90 minutes
                </span>
              )}>
                <IconButton size="large" style={{ cursor: "auto" }} sx={iconButtonStyle}>
                  <InfoIcon style={{ color: "orange" }} />
                </IconButton>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {eventCount > 1 && (
          <Grid container item direction="row" justifyContent={"start"} xs={gridProps.xs} gap={gridProps.props.gap} p={1}>
            {getAssociatedEventItems()}
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default IncidentCardSimple;
